export const installerStorageKey = 'toyama.completion.installer';
export const yearBuckets = [
  { value: 'before_2018', label: 'Before 2018', year: 2017 },
  { value: '2018_2020', label: '2018–20', year: 2020 },
  { value: '2021_2023', label: '2021–23', year: 2023 },
  { value: '2024_plus', label: '2024+', year: 2024 },
] as const;
export const pendingReasons = ['No mobile signal', 'Customer not at site', 'Customer refused', 'Customer busy, asked us to call later', 'Other'] as const;

export function installerFields(name: string) {
  const value = name.trim();
  if (!value) throw new Error('Enter the installer or technician name.');
  return { submitted_by_name: value, installer_name: value };
}
export function oldCustomerDetails(input: { phone: string; name: string; area: string; city: string; year: string; installedBy: string; dealerName: string; vertical: string; installer: string }) {
  const bucket = yearBuckets.find(b => b.value === input.year);
  if (!bucket) throw new Error('Choose roughly when it was installed.');
  const installedBy = input.installedBy === 'toyama' ? 'Toyama' : input.installedBy === 'unknown' ? "Don't know" : input.installedBy === 'dealer' ? input.dealerName.trim() : '';
  if (!installedBy) throw new Error('Choose who installed it and enter the dealer name if applicable.');
  if (![input.phone, input.name, input.area, input.city].every(value => value.trim())) throw new Error('Enter the mobile number, name, area and city.');
  const city = /^(bangalore|bengaluru|blr)$/i.test(input.city.trim()) ? 'Bangalore' : input.city.trim();
  // Numeric year supports older servers; the bucket preserves the actual answer.
  return { phone: input.phone.trim(), name: input.name.trim(), area: input.area.trim(), city, install_year: bucket.year, install_year_bucket: bucket.value, installed_by: installedBy, vertical_slug: input.vertical, ...installerFields(input.installer) };
}
export function pendingDetails(reason: string, note: string, installer: string) {
  if (!pendingReasons.some(value => value === reason)) throw new Error('Choose why feedback could not be collected.');
  return { reason: `${reason}${note.trim() ? ` — ${note.trim()}` : ''}`, ...installerFields(installer) };
}
export interface SendStatus { can_continue_unverified?: boolean; sends_remaining?: number; retry_after_seconds?: number }
export function sendStatus(value: unknown): SendStatus {
  if (!value || typeof value !== 'object') return {};
  const data = value as Record<string, unknown>;
  return {
    can_continue_unverified: data.can_continue_unverified === true,
    ...(typeof data.sends_remaining === 'number' && Number.isFinite(data.sends_remaining) ? { sends_remaining: Math.max(0, Math.floor(data.sends_remaining)) } : {}),
    ...(typeof data.retry_after_seconds === 'number' && Number.isFinite(data.retry_after_seconds) ? { retry_after_seconds: Math.max(0, Math.ceil(data.retry_after_seconds)) } : {}),
  };
}
export interface DeliveryState { sendsRemaining: number; canContinue: boolean; uncertainAttempts: number; resendAt: number }
// This is a retry allowance, not permission to bypass server verification or quotas.
export function initialDeliveryState(): DeliveryState {
  return { sendsRemaining: 3, canContinue: false, uncertainAttempts: 0, resendAt: 0 };
}
export function beginDeliveryAttempt(state: DeliveryState, now: number): DeliveryState {
  return { ...state, resendAt: now + 30000 };
}
export function uncertainDeliveryAttempt(state: DeliveryState): DeliveryState {
  // A lost response cannot tell us whether the server sent an SMS. Keep the cooldown,
  // but do not turn uncertain transport attempts into a permanently exhausted quota.
  return { ...state, uncertainAttempts: state.uncertainAttempts + 1, canContinue: false };
}
export function reconcileDelivery(state: DeliveryState, value: unknown, now: number): DeliveryState {
  const status = sendStatus(value);
  return { ...state, canContinue: status.can_continue_unverified === true,
    sendsRemaining: status.sends_remaining ?? state.sendsRemaining,
    resendAt: status.retry_after_seconds === undefined ? state.resendAt : now + status.retry_after_seconds * 1000 };
}
export function recoverDeliveryVerification(): DeliveryState {
  // Policy changes/expired proofs can leave a stale local quota. Permit a request
  // that reconciles with the server; the server still enforces its durable budget.
  return initialDeliveryState();
}
export function rememberInstaller(storage: Pick<Storage, 'setItem'>, name: string) {
  try { storage.setItem(installerStorageKey, name.trim()); } catch { /* Storage denial must not prevent sign-off. */ }
}
export function recalledInstaller(storage: Pick<Storage, 'getItem'>): string {
  try { return storage.getItem(installerStorageKey) || ''; } catch { return ''; }
}
